import { useEffect, useRef, useState, type FormEvent } from 'react'
import { api, ApiError } from '@api'
import {
	type Activity,
	ActivityName,
	type ActivityLocation,
	type GeocodeResultItem,
} from '@hewey/shared'
import { ActivityMap } from './ActivityMap.tsx'
import { ActivityTagsDialog } from './ActivityTagsDialog.tsx'
import { Dialog } from '@components/Dialog.tsx'
import styles from './AddActivityDialog.module.css'

type Props = {
	open: boolean
	onClose: () => void
	onCreate: (input: {
		name: string
		location: ActivityLocation
	}) => Promise<Activity>
	onPatched: (activity: Activity) => void
}

type WhereKind = 'home' | 'place'

// Geocoder result the user committed to. Coords can drift after this if
// they nudge the marker; displayName stays as the original Nominatim
// string so we know where the friendlyName default came from. `town` stays
// put through a nudge too — the map is at zoom 14, so a drag spans well under
// the locality it was picked in, and re-resolving would cost a geocoder
// request per add.
type Chosen = {
	lat: number
	lng: number
	displayName: string
	town: string | null
}

const DEBOUNCE_MS = 300

// Nominatim's displayName is verbose (e.g. "Chichester Marina, Birdham,
// Chichester, West Sussex, England, PO20 7EJ, United Kingdom"). The
// first comma-segment is usually the right friendly name to pre-fill.
const friendlyDefault = (displayName: string): string => {
	const first = displayName.split(',')[0]?.trim() ?? displayName
	return first.slice(0, 120)
}

export const AddActivityDialog = ({
	open,
	onClose,
	onCreate,
	onPatched,
}: Props) => {
	const inputRef = useRef<HTMLInputElement>(null)

	const [step, setStep] = useState<'details' | 'tags'>('details')
	const [tagged, setTagged] = useState<Activity | null>(null)

	const [name, setName] = useState('')
	const [where, setWhere] = useState<WhereKind>('home')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Place sub-state
	const [query, setQuery] = useState('')
	const [results, setResults] = useState<GeocodeResultItem[]>([])
	const [searching, setSearching] = useState(false)
	const [chosen, setChosen] = useState<Chosen | null>(null)
	const [friendlyName, setFriendlyName] = useState('')

	useEffect(() => {
		if (open) {
			setStep('details')
			setTagged(null)
			setName('')
			setWhere('home')
			setError(null)
			setQuery('')
			setResults([])
			setSearching(false)
			setChosen(null)
			setFriendlyName('')
			queueMicrotask(() => inputRef.current?.focus())
		}
	}, [open])

	// Debounced geocode search. The setTimeout cleanup cancels stale
	// queries that haven't fired yet; in-flight requests can still
	// arrive out of order, so we guard the setResults below with a
	// fresh query check.
	useEffect(() => {
		if (where !== 'place') return
		// User has already picked something; don't re-search until they
		// edit the query (which clears `chosen`).
		if (chosen) return
		const q = query.trim()
		if (q.length < 2) {
			setResults([])
			setSearching(false)
			return
		}
		setSearching(true)
		const timer = setTimeout(async () => {
			try {
				const r = await api.activity.geocode(q)
				// Ignore the response if the user has moved on.
				if (q !== query.trim()) return
				setResults(r.results)
			} catch (e) {
				if (q === query.trim()) {
					setError(
						e instanceof ApiError
							? e.message
							: 'Geocoder lookup failed.',
					)
				}
			} finally {
				if (q === query.trim()) setSearching(false)
			}
		}, DEBOUNCE_MS)
		return () => clearTimeout(timer)
	}, [query, where, chosen])

	const chooseResult = (r: GeocodeResultItem) => {
		setChosen({
			lat: r.lat,
			lng: r.lng,
			displayName: r.displayName,
			town: r.town,
		})
		setFriendlyName(friendlyDefault(r.displayName))
		setQuery(friendlyDefault(r.displayName))
		setResults([])
		setError(null)
	}

	const clearChosen = () => {
		setChosen(null)
		setFriendlyName('')
	}

	const onPinDrag = (lat: number, lng: number) => {
		setChosen((prev) => (prev ? { ...prev, lat, lng } : prev))
	}

	const submit = async (e: FormEvent) => {
		e.preventDefault()
		if (submitting) return
		const parsed = ActivityName.safeParse(name)
		if (!parsed.success) {
			setError(parsed.error.issues[0]?.message ?? 'Invalid name')
			return
		}

		let location: ActivityLocation
		if (where === 'home') {
			location = { kind: 'home' }
		} else {
			if (!chosen) {
				setError('Pick a place from the search results.')
				return
			}
			const friendly = friendlyName.trim()
			if (friendly.length === 0) {
				setError('Give the place a short name.')
				return
			}
			location = {
				kind: 'place',
				lat: chosen.lat,
				lng: chosen.lng,
				friendlyName: friendly,
				town: chosen.town,
			}
		}

		setSubmitting(true)
		setError(null)
		try {
			const created = await onCreate({ name: parsed.data, location })
			setTagged(created)
			setStep('tags')
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: 'Something went wrong adding that activity.',
			)
		} finally {
			setSubmitting(false)
		}
	}

	const canSubmit =
		name.trim().length > 0 &&
		!submitting &&
		(where === 'home' ||
			(chosen !== null && friendlyName.trim().length > 0))

	if (step === 'tags' && tagged) {
		return (
			<ActivityTagsDialog
				open={open}
				activity={tagged}
				onPatched={(updated) => {
					setTagged(updated)
					onPatched(updated)
				}}
				onDone={onClose}
				onClose={onClose}
			/>
		)
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<form className={styles.panel} onSubmit={submit}>
				<h2 className={styles.heading}>Add an activity</h2>

				<label className={styles.field}>
					<span className={styles.label}>Name</span>
					<input
						ref={inputRef}
						className={styles.input}
						value={name}
						maxLength={100}
						placeholder="Make muffins"
						onChange={(e) => {
							setName(e.target.value)
							setError(null)
						}}
					/>
				</label>

				<div className={styles.field}>
					<span className={styles.label}>Where</span>
					<div
						className={styles.segmented}
						role="group"
						aria-label="Where"
					>
						<button
							type="button"
							className={`${styles.segment} ${where === 'home' ? styles.segmentActive : ''}`.trim()}
							aria-pressed={where === 'home'}
							onClick={() => setWhere('home')}
						>
							🏠 Home
						</button>
						<button
							type="button"
							className={`${styles.segment} ${where === 'place' ? styles.segmentActive : ''}`.trim()}
							aria-pressed={where === 'place'}
							onClick={() => setWhere('place')}
						>
							📍 Place
						</button>
					</div>
				</div>

				{where === 'place' ? (
					<div className={styles.field}>
						<span className={styles.label}>Search</span>
						<input
							className={styles.input}
							value={query}
							placeholder="Name or postcode (e.g. SO31 1BH)"
							onChange={(e) => {
								setQuery(e.target.value)
								if (chosen) clearChosen()
								setError(null)
							}}
						/>

						{searching ? (
							<p className={styles.hint}>Searching…</p>
						) : null}

						{!chosen && results.length > 0 ? (
							<ul className={styles.results}>
								{results.map((r) => (
									<li key={`${r.lat},${r.lng}`}>
										<button
											type="button"
											className={styles.result}
											onClick={() => chooseResult(r)}
										>
											{r.displayName}
										</button>
									</li>
								))}
							</ul>
						) : null}

						{!chosen &&
						!searching &&
						query.trim().length >= 2 &&
						results.length === 0 ? (
							<p className={styles.hint}>
								No matches — try a different phrase.
							</p>
						) : null}

						{chosen ? (
							<>
								<ActivityMap
									lat={chosen.lat}
									lng={chosen.lng}
									onChange={onPinDrag}
									className={styles.mapInDialog}
								/>
								<p className={styles.hint}>
									Drag the pin to nudge.
								</p>

								<label className={styles.field}>
									<span className={styles.label}>
										Friendly name
									</span>
									<input
										className={styles.input}
										value={friendlyName}
										maxLength={120}
										placeholder="Chichester Marina"
										onChange={(e) =>
											setFriendlyName(e.target.value)
										}
									/>
								</label>
							</>
						) : null}
					</div>
				) : null}

				{error ? <p className={styles.error}>{error}</p> : null}

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.cancel}
						onClick={onClose}
					>
						Cancel
					</button>
					<button
						type="submit"
						className={styles.submit}
						disabled={!canSubmit}
					>
						{submitting ? 'Adding…' : 'Add'}
					</button>
				</div>
			</form>
		</Dialog>
	)
}
